import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Save, FolderOpen, Trash2, Play, Edit2, Check, X, Cloud, CloudOff, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import NeonButton from "@/components/NeonButton";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCAL_STORAGE_KEY = "subspace_presets";

const PresetManager = ({ 
  currentSettings, 
  onLoadPreset,
  isPlaying 
}) => {
  const { isAuthenticated, token } = useAuth();
  const [presets, setPresets] = useState([]);
  const [cloudPresets, setCloudPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [saveToCloud, setSaveToCloud] = useState(true);

  // Load local presets
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        setPresets(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load local presets:", e);
      }
    }
  }, []);

  // Load cloud presets when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCloudPresets();
    } else {
      setCloudPresets([]);
    }
  }, [isAuthenticated, token]);

  const fetchCloudPresets = async () => {
    if (!isAuthenticated) return;
    
    setSyncing(true);
    try {
      const response = await axios.get(`${API}/presets`);
      setCloudPresets(response.data);
    } catch (error) {
      console.error("Failed to fetch cloud presets:", error);
    } finally {
      setSyncing(false);
    }
  };

  // Save to localStorage
  const saveToStorage = (newPresets) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPresets));
    setPresets(newPresets);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error("Please enter a preset name");
      return;
    }

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      settings: currentSettings,
      createdAt: new Date().toISOString(),
      isCloud: false
    };

    // Save locally
    const newPresets = [newPreset, ...presets];
    saveToStorage(newPresets);

    // Save to cloud if authenticated and selected
    if (isAuthenticated && saveToCloud) {
      try {
        const response = await axios.post(`${API}/presets`, {
          name: newPreset.name,
          settings: newPreset.settings
        });
        
        setCloudPresets(prev => [response.data, ...prev]);
        toast.success(`Preset "${newPreset.name}" saved to cloud!`);
      } catch (error) {
        console.error("Failed to save to cloud:", error);
        toast.warning("Saved locally. Cloud sync failed.");
      }
    } else {
      toast.success(`Preset "${newPreset.name}" saved locally`);
    }

    setPresetName("");
    setShowSaveDialog(false);
  };

  const handleLoadPreset = (preset) => {
    onLoadPreset(preset.settings);
    toast.success(`Loaded "${preset.name}"`);
  };

  const handleDeletePreset = async (preset) => {
    // Delete locally
    const newPresets = presets.filter(p => p.id !== preset.id);
    saveToStorage(newPresets);

    // Delete from cloud if it exists there
    if (preset.isCloud && isAuthenticated) {
      try {
        await axios.delete(`${API}/presets/${preset.id}`);
        setCloudPresets(prev => prev.filter(p => p.id !== preset.id));
      } catch (error) {
        console.error("Failed to delete from cloud:", error);
      }
    }

    toast.success("Preset deleted");
  };

  const handleDeleteCloudPreset = async (preset) => {
    if (!isAuthenticated) return;

    try {
      await axios.delete(`${API}/presets/${preset.id}`);
      setCloudPresets(prev => prev.filter(p => p.id !== preset.id));
      toast.success("Cloud preset deleted");
    } catch (error) {
      console.error("Failed to delete cloud preset:", error);
      toast.error("Failed to delete preset");
    }
  };

  const handleRenamePreset = (id) => {
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }

    const newPresets = presets.map(p => 
      p.id === id ? { ...p, name: editName.trim() } : p
    );
    saveToStorage(newPresets);
    setEditingId(null);
    toast.success("Preset renamed");
  };

  const startEditing = (preset) => {
    setEditingId(preset.id);
    setEditName(preset.name);
  };

  const allPresets = [
    ...cloudPresets.map(p => ({ ...p, isCloud: true })),
    ...presets.filter(p => !cloudPresets.find(c => c.name === p.name))
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <FolderOpen size={16} className="text-violet-400" />
            Sound Presets
          </h4>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            {isAuthenticated ? (
              <>
                <Cloud size={10} className="text-cyan-400" />
                Cloud sync enabled
              </>
            ) : (
              <>
                <CloudOff size={10} />
                Local storage only
              </>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {isAuthenticated && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchCloudPresets}
              disabled={syncing}
              className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
            >
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            </motion.button>
          )}
          <NeonButton 
            size="sm" 
            onClick={() => setShowSaveDialog(true)}
            data-testid="save-preset-btn"
          >
            <Save size={14} className="mr-1" />
            Save
          </NeonButton>
        </div>
      </div>

      {/* Save Dialog */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-4 space-y-3"
          >
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Enter preset name..."
              data-testid="preset-name-input"
              className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500/50"
              autoFocus
            />
            
            {isAuthenticated && (
              <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveToCloud}
                  onChange={(e) => setSaveToCloud(e.target.checked)}
                  className="rounded border-white/20"
                />
                <Cloud size={12} className="text-cyan-400" />
                Save to cloud
              </label>
            )}
            
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSavePreset}
                data-testid="confirm-save-preset"
                className="flex-1 py-2 bg-violet-600 text-white rounded-lg text-sm flex items-center justify-center gap-1"
              >
                <Check size={14} />
                Save
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSaveDialog(false)}
                className="p-2 bg-white/10 text-gray-400 rounded-lg"
              >
                <X size={18} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Presets List */}
      {allPresets.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          No presets saved yet
        </div>
      ) : (
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {allPresets.map((preset) => (
            <motion.div
              key={preset.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-3 flex items-center justify-between group"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                  preset.isCloud ? "bg-cyan-500/20" : "bg-violet-500/20"
                )}>
                  {preset.isCloud ? (
                    <Cloud size={14} className="text-cyan-400" />
                  ) : (
                    <FolderOpen size={14} className="text-violet-400" />
                  )}
                </div>
                
                {editingId === preset.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-2 py-1 bg-black/30 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-violet-500/50"
                      autoFocus
                    />
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRenamePreset(preset.id)}
                      className="p-1 text-green-400"
                    >
                      <Check size={14} />
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setEditingId(null)}
                      className="p-1 text-gray-400"
                    >
                      <X size={14} />
                    </motion.button>
                  </div>
                ) : (
                  <div className="min-w-0">
                    <span className="text-sm text-white truncate block">{preset.name}</span>
                    <span className="text-xs text-gray-500">
                      {preset.isCloud ? "Cloud" : "Local"}
                    </span>
                  </div>
                )}
              </div>

              {editingId !== preset.id && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleLoadPreset(preset)}
                    data-testid={`load-preset-${preset.id}`}
                    className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
                  >
                    <Play size={14} />
                  </motion.button>
                  {!preset.isCloud && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => startEditing(preset)}
                      className="p-2 text-gray-400 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Edit2 size={14} />
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => preset.isCloud ? handleDeleteCloudPreset(preset) : handleDeletePreset(preset)}
                    data-testid={`delete-preset-${preset.id}`}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 size={14} />
                  </motion.button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PresetManager;
